import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Loader2 } from 'lucide-react';
import { format, isAfter } from 'date-fns';
import DateRangePicker from '@/components/ui/DateRangePicker';
import { safeParseDate } from '@/lib/utils';

function TransitionReportPopup({ text, closeRawTransactionPopup, wholesaler_name, wholeSaler_id, unique_ObjectId }) {
    const startDateInit = new Date();
    startDateInit.setDate(startDateInit.getDate() - 10);
    
    const [startDate, setStartDate] = useState(format(startDateInit, "MM/dd/yyyy"));
    const [endDate, setEndDate] = useState(format(new Date(), "MM/dd/yyyy"));
    const [selectedDistributor, setSelectedDistributor] = useState('All');
    const [processors, setProcessors] = useState([
        { id: 1, value: "1", isChecked: true, pname: "CRX" },
        { id: 2, value: "2", isChecked: true, pname: "HRX" },
        { id: 3, value: "3", isChecked: true, pname: "HDX" },
        { id: 4, value: "4", isChecked: true, pname: "GH" }
    ]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [rawTool, setRawTool] = useState(null);

    useEffect(() => {
        fetchRawTool();
    }, []);

    const fetchRawTool = async () => {
        try {
            const response = await fetch('/api/ws/RawTransactionTool', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'X-Browser-Url': window.location.href 
                },
                body: JSON.stringify({
                    whname: wholesaler_name,
                    wh_id: wholeSaler_id,
                    mname: ''
                })
            });
            const data = await response.json();
            if (data?.length > 0) {
                setRawTool(data);
            }
        } catch (err) {
        }
    };

    const handleCalendar = useCallback((e, picker) => {
        const changedStartDate = format(safeParseDate(picker.startDate), "MM/dd/yyyy");
        const changedEndDate = format(safeParseDate(picker.endDate), "MM/dd/yyyy");
        
        if (isAfter(safeParseDate(changedEndDate), new Date())) {
            setError('Cannot select dates after today');
            return;
        }
        
        setStartDate(changedStartDate);
        setEndDate(changedEndDate);
        setError('');
    }, []);

    const handleProcessorChange = useCallback((value, checked) => {
        setProcessors(prev => prev.map(p => 
            p.value === value ? { ...p, isChecked: checked } : p
        ));
        setError('');
    }, []);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        setError('');
        
        const checkedProcessors = processors.filter(p => p.isChecked);
        if (checkedProcessors.length === 0) {
            setError('Please select at least one data source');
            return;
        }
        
        const msDiff = new Date(endDate).getTime() - new Date(startDate).getTime();
        const dateRange = Math.floor(msDiff / (1000 * 60 * 60 * 24));
        
        if (dateRange > 31) {
            setError('Date range must be up to 31 days');
            return;
        }
        
        setLoading(true);
        
        const plist = checkedProcessors.map(p => p.value).join(',');
        
        try {
            const response = await fetch('/api/ws/rawtransactionstatus', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'X-Browser-Url': window.location.href 
                },
                body: JSON.stringify({
                    wname: wholesaler_name,
                    wh_id: unique_ObjectId,
                    dname: selectedDistributor,
                    pcn: plist,
                    df: startDate,
                    dt: endDate
                })
            });
            
            const data = await response.json();
            
            if (data?.length > 0) {
                if (data[0].maxValue <= 0) {
                    setError(data[0].data);
                } else {
                    // Download report
                    const originLocation = window.location.protocol + '//' + window.location.host;
                    const url = `${originLocation}/report/rawtransaction?wname=${wholesaler_name}&wh_id=${unique_ObjectId}&dname=${selectedDistributor}&pcn=${plist}&df=${startDate}&dt=${endDate}`;
                    window.open(url, '_blank');
                }
            } else {
                setError('Error! Please try later.');
            }
        } catch (err) {
            setError('Error! Please try later.');
        } finally {
            setLoading(false);
        }
    }, [processors, startDate, endDate, selectedDistributor, wholesaler_name, unique_ObjectId]);

    const handleOpenChange = useCallback((open) => {
        if (!open) {
            closeRawTransactionPopup();
        }
    }, [closeRawTransactionPopup]);

    return (
        <Dialog open={true} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-4xl">
                <DialogHeader>
                    <DialogTitle>{text}</DialogTitle>
                </DialogHeader>
                
                <div className="flex flex-col gap-4 mt-3">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                            {/* Distributor Select */}
                            <div className="flex flex-col gap-2">
                                <Label>Select a Distributor</Label>
                                <Select value={selectedDistributor} onValueChange={setSelectedDistributor}>
                                    <SelectTrigger className="mt-2 h-10 max-w-[200px]">
                                        <SelectValue placeholder="Select distributor" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="All">All</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            
                            {/* Data Sources */}
                            <div className="flex flex-col gap-2">
                                <Label>Select Data Source(s)</Label>
                                <div className="flex flex-nowrap gap-3 mt-2 h-10 items-center">
                                    {processors.map((p) => (
                                        <div key={p.id} className="flex items-center gap-2">
                                            <Checkbox
                                                id={p.pname}
                                                checked={p.isChecked}
                                                onCheckedChange={(checked) => handleProcessorChange(p.value, checked)}
                                            />
                                            <Label htmlFor={p.pname} className="cursor-pointer whitespace-nowrap">{p.pname}</Label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            
                            {/* Date Range */}
                            <div className="flex flex-col gap-2">
                                <Label>Date Range</Label>
                                <DateRangePicker 
                                    className="w-full"
                                    compact={true}
                                    side="bottom"
                                    popoverClassName="[&_[data-slot=calendar]]:!min-w-0 [&_[data-slot=calendar]]:!max-w-full [&_[data-slot=calendar]]:w-auto"
                                    calendarClassName="p-1 [--cell-size:1.8rem]"
                                    calendarClassNames={{
                                        months: "flex flex-col gap-1 md:flex-row",
                                        month: "flex w-full flex-col gap-1",
                                        week: "flex w-full",
                                        nav: "absolute inset-x-0 top-0 flex w-full items-center justify-between gap-1 h-7 px-1",
                                        dropdowns: "flex h-7 w-full items-center justify-center gap-1.5 text-[11px] font-semibold",
                                        dropdown_root: "relative rounded-md border bg-card px-1.5 py-0.5 min-w-[65px] cursor-pointer hover:bg-accent transition-colors h-6 flex items-center text-[11px]",
                                        caption_label: "select-none font-medium [&>svg]:text-muted-foreground flex h-6 items-center gap-1 rounded-md pl-1.5 pr-0.5 text-[11px] [&>svg]:size-3",
                                        month_caption: "flex h-7 w-full items-center justify-center px-7",
                                        weekdays: "flex",
                                        weekday: "text-muted-foreground w-[1.8rem] h-[1.8rem] select-none text-[11px] font-medium flex items-center justify-center",
                                        day: "w-[1.8rem] h-[1.8rem] aspect-square p-0 text-center select-none group/day relative font-medium text-[11px]",
                                        button_previous: "h-4 w-4 select-none p-0 aria-disabled:hidden hover:bg-accent [&>svg]:size-4 mt-2 rounded-none",
                                        button_next: "h-4 w-4 select-none p-0 aria-disabled:opacity-50 hover:bg-accent [&>svg]:size-4 mt-2 rounded-none ms-auto",
                                    }}
                                    initialSettings={{
                                        startDate: startDate,
                                        endDate: endDate,
                                        maxDate: new Date(),
                                        locale: { format: 'MM/DD/YYYY' }
                                    }}
                                    onApply={handleCalendar}
                                >
                                    <div className="flex items-center gap-0 w-full rounded-md overflow-hidden border border-input bg-card hover:shadow-sm transition-shadow mt-2 h-10">
                                        <div className="flex items-center gap-1 px-3 flex-1">
                                            <span className="text-sm text-muted-foreground whitespace-nowrap">From</span>
                                            <Input
                                                type="text"
                                                className="bg-transparent text-sm cursor-pointer w-full !border-0 outline-none h-auto p-0 rounded-none"
                                                value={startDate}
                                                readOnly
                                            />
                                        </div>
                                        <div className="h-6 w-px bg-border flex-shrink-0" />
                                        <div className="flex items-center gap-1 px-3 flex-1">
                                            <span className="text-sm text-muted-foreground whitespace-nowrap">To</span>
                                            <Input
                                                type="text"
                                                className="bg-transparent text-sm cursor-pointer w-full !border-0 outline-none h-auto p-0 rounded-none"
                                                value={endDate}
                                                readOnly
                                            />
                                        </div>
                                    </div>
                                </DateRangePicker>
                            </div>
                        </div>
                        
                        {error && (
                            <p className="text-sm text-destructive">{error}</p>
                        )}
                    </form>
                </div>

                <DialogFooter>
                    <Button type="submit" disabled={loading} onClick={handleSubmit}>
                        {loading ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Generating...
                            </>
                        ) : (
                            'Generate Report'
                        )}
                    </Button>
                    <Button type="button" variant="secondary" onClick={closeRawTransactionPopup}>
                        Cancel
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default TransitionReportPopup;
